import React, { useCallback, useMemo, useState } from 'react';
import { useEcoLayoutTuning } from '../../contexts/EcoLayoutTuningContext';
import { useJuniorAliveNav } from '../../contexts/JuniorAliveNavContext';
import {
  buildExerciseFromConfig,
  clampMeasureCount,
  createDefaultSessionConfig,
  emptyPattern,
  isPatternComplete,
  resizePatternForTimeSignature,
  setSlotFigure,
  wagonRef,
  type EcoRitmoExercise,
  type EcoRitmoSessionConfig,
  type EcoSoundId,
  type TimeSignature,
  type WagonRef,
} from '../../lib/rhythm/ecoRitmoConfig';
import type { RhythmFigureId } from '../../lib/rhythm/rhythmTokens';
import {
  loadEcoMicSettings,
  saveEcoMicSettings,
  type EcoMicSettings,
} from '../../lib/rhythm/ecoMicConfig';
import { EcoBlockPalette } from './EcoBlockPalette';
import { EcoGlobalSettings } from './EcoGlobalSettings';
import { EcoLayoutTuner } from './EcoLayoutTuner';
import { EcoTrainZone } from './EcoTrainZone';

interface EcoRitmoBuilderProps {
  onStart: (exercise: EcoRitmoExercise) => void;
}

export const EcoRitmoBuilder: React.FC<EcoRitmoBuilderProps> = ({ onStart }) => {
  const { navigateAlive } = useJuniorAliveNav();
  const { cssVarsFor } = useEcoLayoutTuning();
  const [config, setConfig] = useState<EcoRitmoSessionConfig>(createDefaultSessionConfig);
  const [micSettings, setMicSettings] = useState<EcoMicSettings>(() => loadEcoMicSettings());
  const [selectedWagon, setSelectedWagon] = useState<WagonRef>(wagonRef(0, 0));
  const [dragFigure, setDragFigure] = useState<RhythmFigureId | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showTuner, setShowTuner] = useState(false);

  const canPlay = isPatternComplete(config.pattern, config.timeSignature);

  const setFigure = useCallback((ref: WagonRef, fig: RhythmFigureId | null) => {
    setConfig((prev) => {
      const next = setSlotFigure(prev.pattern, ref, fig, prev.timeSignature);
      return next ? { ...prev, pattern: next } : prev;
    });
  }, []);

  const addFigure = useCallback(
    (fig: RhythmFigureId) => {
      setFigure(selectedWagon, fig);
    },
    [selectedWagon, setFigure]
  );

  const handleMeasureCountChange = (count: number) => {
    const next = clampMeasureCount(count);
    setConfig((prev) => {
      let pattern = prev.pattern;
      if (next > prev.measureCount) {
        pattern = [...pattern, ...emptyPattern(next - prev.measureCount, prev.timeSignature)];
      } else {
        pattern = pattern.slice(0, next);
      }
      return { ...prev, measureCount: next, pattern };
    });
    if (selectedWagon.measureIndex >= next) {
      setSelectedWagon(wagonRef(Math.max(0, next - 1), 0));
    }
  };

  const handleTimeSignature = (timeSignature: TimeSignature) => {
    setConfig((prev) => ({
      ...prev,
      timeSignature,
      pattern: resizePatternForTimeSignature(prev.pattern, timeSignature),
    }));
    setSelectedWagon(wagonRef(0, 0));
  };

  const handleMicSettings = useCallback((next: EcoMicSettings) => {
    setMicSettings(next);
    saveEcoMicSettings(next);
  }, []);

  const exercise = useMemo(() => buildExerciseFromConfig(config), [config]);

  const layoutCssVars = useMemo(
    () => cssVarsFor(config.timeSignature, config.measureCount),
    [cssVarsFor, config.timeSignature, config.measureCount]
  );

  return (
    <div
      className="eco-builder flex flex-col flex-1 min-h-0 overflow-hidden"
      style={layoutCssVars as React.CSSProperties}
    >
      <header className="eco-builder-header shrink-0 flex items-center gap-2 px-2 py-1">
        <button
          type="button"
          onClick={() => navigateAlive('/ritmo')}
          className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-lg"
          aria-label="Volver a Ritmo"
        >
          ←
        </button>

        <h1 className="eco-builder-title flex-1 text-center junior-display text-base sm:text-xl leading-tight">
          CREA TU RITMO ECO
        </h1>

        <button
          type="button"
          onClick={() => setShowTuner((v) => !v)}
          className={`w-9 h-9 rounded-full text-sm shrink-0 font-medium ${showTuner ? 'eco-tuner-toggle--active' : ''}`}
          aria-label="Ajustar layout"
          title="Ajustar layout del tren"
        >
          ⚙
        </button>

        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="w-9 h-9 rounded-full text-sm shrink-0 font-medium"
          aria-label="Ayuda"
        >
          ?
        </button>
      </header>

      {showHelp && (
        <div className="eco-help shrink-0 mx-2 px-3 py-1.5 rounded-xl text-[11px] leading-snug">
          Cada vagón es una figura. En 4/4, 4 vagones = 1 compás. Completa todos los compases y pulsa
          PLAY abajo.
        </div>
      )}

      <div className="eco-builder-body zona-central flex-1 min-h-0 flex gap-2 sm:gap-3 overflow-hidden px-2 relative">
        <EcoBlockPalette
          config={config}
          selectedWagon={selectedWagon}
          onAddFigure={addFigure}
          dragFigure={dragFigure}
          onDragFigure={setDragFigure}
        />
        <EcoTrainZone
          config={config}
          selectedWagon={selectedWagon}
          onSelectWagon={setSelectedWagon}
          onSetFigure={setFigure}
          onMeasureCountChange={handleMeasureCountChange}
        />
        <EcoLayoutTuner
          timeSignature={config.timeSignature}
          measureCount={config.measureCount}
          open={showTuner}
          onClose={() => setShowTuner(false)}
        />
      </div>

      <EcoGlobalSettings
        config={config}
        micSettings={micSettings}
        onTimeSignature={handleTimeSignature}
        onBpm={(bpm) => setConfig((prev) => ({ ...prev, bpm }))}
        onSound={(soundId: EcoSoundId) => setConfig((prev) => ({ ...prev, soundId }))}
        onMicSettings={handleMicSettings}
        canPlay={canPlay}
        onPlay={() => onStart(exercise)}
      />
    </div>
  );
};

export default EcoRitmoBuilder;
